import React, { useCallback, useEffect, useMemo } from 'react'
import { AdminPanelSettings, BusinessCenter, PieChart } from '@mui/icons-material'
import { StyledPageContent } from '../../styles/PageStyles'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import MasonryTileTitle from '../../components/Masonry/MasonryTileTitle'
import { useAppDispatch, useAppSelector } from '../../store/redux/hooks'
import { PageIndex, setPageIndex, setPageName } from '../../store/redux/reducers/pageNameSlice'
import { useNavigate } from 'react-router-dom'
import navigation from '../../config/navigation'
import { pageHeaderActions } from '../../store/redux/reducers/pageHeaderSlice'
import { getSelectedDate, monthPickerActions } from '../../store/redux/reducers/monthPickerSlice'
import AddWorklogForm from './AddWorklogForm'
import {
  PlannedHours,
  TransformedPlannedHours,
  useGetScheduleQuery,
} from '../../store/redux/reducers/scheduleDateSlice'
import {
  getCurrentUser,
  getIsBaseUser,
  getIsEmployee,
  getIsLeaderAndNotManagement,
  getIsLeaderOrManagement,
  getIsManagement,
  getIsProjectLeader,
} from '../../store/redux/reducers/currentUserSlice'
import MasonryTileHoursHome from './MasonryTileHoursHome'
import { Box, Grid, Typography } from '@mui/material'
import MasonryTile from '../../components/Masonry/MasonryTile'
import { useGetUsersWorklogQuery } from '../../store/redux/reducers/worklogSlice'
import DateFormat from '../../store/types/enums/DateFormat'
import Loading from '../../components/Loading/Loading'
import ProjectsCarousel from './ProjectsCarousel'
import { useGetAcronymsQuery } from '../../store/redux/reducers/projectsSlice'
import { useSelector } from 'react-redux'
import WorklogDailyEntries from '../../components/Worklog/WorklogDailyEntries'

const HomeView = () => {
  const selectedDate = useAppSelector(state => getSelectedDate(state))
  const currentUser = useSelector(getCurrentUser)
  const isLeaderOrManagement = useSelector(getIsLeaderOrManagement)
  const isEmployee = useSelector(getIsEmployee)
  const isProjectLeader = useSelector(getIsProjectLeader)
  const isLeaderAndNotManagement = useSelector(getIsLeaderAndNotManagement)
  const isBaseUser = useSelector(getIsBaseUser)
  const isManagement = useSelector(getIsManagement)

  const isLeadersOrManagement = isLeaderOrManagement || isProjectLeader
  const isOnlyEmployee = isEmployee && !isLeadersOrManagement
  const isOnlyLeader = isLeaderAndNotManagement
  const employeeId = currentUser?.id === undefined ? '' : currentUser.id
  const formattedDate =
    selectedDate.length > 0 ? dayjs(selectedDate).format(DateFormat.YearMonth) : dayjs().format(DateFormat.YearMonth)

  const { data: scheduleResult, isLoading: isScheduleLoading } = useGetScheduleQuery(
    {
      employee: employeeId,
      date: formattedDate,
    },
    { skip: isBaseUser }
  )

  const { data: worklog, isLoading: isWorklogLoading } = useGetUsersWorklogQuery(
    {
      employeeId: employeeId,
      date: formattedDate,
    },
    { skip: isBaseUser }
  )

  const { data: projectsResponse } = useGetAcronymsQuery(
    {
      page: 0,
      size: 5,
      favorite: true,
    },
    { skip: isBaseUser || isOnlyEmployee }
  )

  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()

  useEffect(() => {
    dispatch(setPageName(t('page.name.home')))
    dispatch(setPageIndex(PageIndex.HOME))
    dispatch(pageHeaderActions.setInitialState())
    dispatch(monthPickerActions.setDate(dayjs().toString()))
  }, [dispatch, t])

  const convertToPlannedHours = useCallback(
    (transformed: TransformedPlannedHours[]) => {
      const plannedHours: PlannedHours = {}

      for (let i = 1; i <= dayjs(selectedDate).daysInMonth(); i++) {
        const day = dayjs(selectedDate).set('date', i)

        plannedHours[day.toISOString().split('T')[0]] = [
          {
            id: Math.random().toString(),
            from: '-',
            to: '-',
          },
        ]
      }

      for (const entry of transformed) {
        const { date, timeSlots } = entry
        plannedHours[date] = timeSlots.map(timeSlot => {
          const { id, from, to } = timeSlot
          return {
            id,
            from: from ? from.slice(0, -3) : '',
            to: to ? to.slice(0, -3) : '',
          }
        })
      }

      return plannedHours
    },
    [selectedDate]
  )

  const plannedHours = useMemo(() => {
    if (scheduleResult) {
      if (currentUser?.student) {
        return convertToPlannedHours(scheduleResult.plannedHours)
      } else {
        return convertToPlannedHours(scheduleResult.trackedHours)
      }
    } else return {}
  }, [scheduleResult, convertToPlannedHours, currentUser?.student])

  return isScheduleLoading || isWorklogLoading ? (
    <Loading />
  ) : (
    <StyledPageContent>
      {!isBaseUser ? (
        <Grid
          container
          columns={{
            xs: 4,
            sm: 4,
            md: 4,
            lg: 8,
            xl: isLeaderOrManagement ? 12 : 8,
          }}
          spacing={3}
          sx={{ maxHeight: { md: 'none', lg: 1800 }, paddingBottom: 3 }}
        >
          <Grid item xs={4} maxWidth={'none'} sx={{ width: '100%' }}>
            <MasonryTileHoursHome hours={plannedHours} worklog={worklog} />
          </Grid>

          <Grid item container xs={12} lg={6} xl={isLeaderOrManagement ? 8 : 6} height={'fit-content'} spacing={3}>
            <Grid item xs={12} xl={isLeaderOrManagement ? 6 : 12}>
              <AddWorklogForm />
            </Grid>
            <Grid item container sm={12} xl={6} height={'fit-content'} spacing={2}>
              {isLeaderOrManagement && projectsResponse && projectsResponse.totalElements > 0 && (
                <Grid item xs={12}>
                  <MasonryTile>
                    <ProjectsCarousel />
                  </MasonryTile>
                </Grid>
              )}
              <Grid
                item
                container
                sx={{
                  display: isLeaderOrManagement ? 'auto' : 'none',
                }}
                spacing={2}
              >
                {isManagement && (
                  <Grid item xs={12} md={4} lg={4}>
                    <MasonryTile onClick={() => navigate(navigation.general)} sx={{ width: '100%' }}>
                      <MasonryTileTitle
                        title={t('components.tile.title.administration')}
                        icon={AdminPanelSettings}
                        sx={{ flexDirection: 'column' }}
                      />
                    </MasonryTile>
                  </Grid>
                )}

                {isManagement && (
                  <Grid item xs={12} md={4} lg={4}>
                    <MasonryTile onClick={() => navigate(navigation.candidates)} sx={{ width: '100%' }}>
                      <MasonryTileTitle
                        title={t('components.tile.title.recruitment')}
                        icon={BusinessCenter}
                        sx={{ flexDirection: 'column' }}
                      />
                    </MasonryTile>
                  </Grid>
                )}

                {isLeaderOrManagement && (
                  <Grid item xs={12} md={isOnlyLeader ? 12 : 4} lg={isOnlyLeader ? 12 : 4}>
                    <MasonryTile onClick={() => navigate(navigation.reports)} sx={{ width: '100%' }}>
                      <MasonryTileTitle
                        title={t('components.tile.title.reports')}
                        icon={PieChart}
                        sx={{ flexDirection: 'column' }}
                      />
                    </MasonryTile>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid item xs={12} xl={isLeaderOrManagement ? 6 : 12}>
              <WorklogDailyEntries />
            </Grid>
          </Grid>
        </Grid>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Typography variant="h5" sx={{ color: 'text.light' }}>
            {t('homeContact')}
          </Typography>
        </Box>
      )}
    </StyledPageContent>
  )
}

export default HomeView
